import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format, isValid } from 'date-fns'
import { Loader2, RefreshCw } from 'lucide-react'
import { FormEvent, useState } from 'react'

import {
  ACTIVE_REFRESH_STATUSES,
  Refresh,
  RefreshStatus,
  configAnalyticsClient
} from '@/api/config-analytics-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { toast } from '@/components/ui/use-toast'

const POLL_MS = 10_000
const REFRESHES_KEY = ['config-analytics', 'refreshes']

const statusLabel: Record<RefreshStatus, string> = {
  capturing: 'Capturing',
  ingesting: 'Ingesting',
  complete: 'Done',
  partial: 'Done (partial)',
  failed: 'Failed'
}

const isActive = (refresh: Refresh) =>
  (ACTIVE_REFRESH_STATUSES as readonly string[]).includes(refresh.status)

const formatTime = (iso?: string) => {
  if (!iso) return '—'

  const date = new Date(iso)

  return isValid(date) ? format(date, 'dd MMM, HH:mm') : iso
}

const outcome = (refresh: Refresh) => {
  if (refresh.status === 'failed') return refresh.error ?? 'Failed'
  if (refresh.resource_count !== undefined)
    return `${refresh.resource_count} configs`

  return '—'
}

/**
 * Re-capture and ingest one org now instead of waiting for the nightly run,
 * e.g. to check a fix. Admins only (the API answers 403 for everyone else).
 */
export const RefreshSection = () => {
  const [orgId, setOrgId] = useState('')
  const [busy, setBusy] = useState(false)
  const queryClient = useQueryClient()

  // Polls while a refresh runs. The list is a convenience: starting a
  // refresh reports its own errors.
  const { data: refreshes = [] } = useQuery({
    queryKey: REFRESHES_KEY,
    queryFn: () => configAnalyticsClient.listRefreshes(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isActive) ? POLL_MS : false
  })

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    try {
      const refresh = await configAnalyticsClient.createRefresh(orgId.trim())

      toast({
        title: refresh.already_running
          ? `A refresh of org ${refresh.org_id} is already running`
          : `Refreshing org ${refresh.org_id}`,
        description:
          'Captures and ingests the org now. Usually a few minutes, larger orgs longer.'
      })
      setOrgId('')
      await queryClient.invalidateQueries({ queryKey: REFRESHES_KEY })
    } catch (error) {
      toast({
        title: 'Could not refresh',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive'
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-primary">Refresh an org</h2>
        <p className="text-sm text-muted-foreground">
          The data is as of the last nightly snapshot. Refresh an org to capture
          and ingest its configuration now, e.g. to check a fix. Only orgs
          enabled for config analytics; agents can do the same with the{' '}
          <code>refresh_org</code> tool.
        </p>
      </div>

      <form className="flex items-end gap-2" onSubmit={(e) => void submit(e)}>
        <div className="space-y-1">
          <Label htmlFor="refresh-org-id">Org ID</Label>
          <Input
            className="w-48"
            id="refresh-org-id"
            inputMode="numeric"
            onChange={(event) => setOrgId(event.target.value)}
            pattern="[0-9]{1,20}"
            placeholder="e.g. 739224"
            value={orgId}
          />
        </div>
        <Button
          disabled={busy || !/^[0-9]{1,20}$/.test(orgId.trim())}
          type="submit"
        >
          {busy ? (
            <Loader2 className="mr-2 animate-spin" size={14} />
          ) : (
            <RefreshCw className="mr-2" size={14} />
          )}
          Refresh
        </Button>
      </form>

      {refreshes.length > 0 && (
        <Table className="text-primary">
          <TableHeader>
            <TableRow>
              <TableHead>Org</TableHead>
              <TableHead>Requested by</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {refreshes.map((refresh) => (
              <TableRow key={refresh.id}>
                <TableCell>{refresh.org_id}</TableCell>
                <TableCell>
                  {refresh.requested_by.email ?? refresh.requested_by.user_id}
                  {refresh.requested_by.via === 'mcp' &&
                    ` (${refresh.requested_by.client_name ?? 'MCP'})`}
                </TableCell>
                <TableCell>{formatTime(refresh.created_at)}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      refresh.status === 'failed'
                        ? 'destructive'
                        : isActive(refresh)
                        ? 'outline'
                        : 'default'
                    }
                  >
                    {isActive(refresh) && (
                      <Loader2 className="mr-1 animate-spin" size={12} />
                    )}
                    {statusLabel[refresh.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-xs break-words">
                  {outcome(refresh)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}
